import { nextClientCastSequence } from '../systems/combat/ClientCastSequence';
import { EventBus, EVENTS } from '../../shared/EventBus';
/**
 * ActionDispatcher
 * Handles HUD action dispatch — routes action strings to the correct game system.
 *
 * Extracted from Game.tsx to reduce component size.
 * Takes callback interfaces instead of direct React state setters.
 */

import { Helix2000Character } from '../../shared/CharacterTypes';
import { gameLogger } from '../../utils/GameLogger';
import { readPlayerVitals } from '../systems/player/livePlayerVitals';
import { classIcon } from '../ui/theme';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { GameScene } from '../scenes/GameScene';
import { chatHandler } from '../ui/contexts/ChatContext';
import { storage } from '../services/StorageService';
import { windowBridge } from '../ui/framework/windowBridge';
import { ChatCommandProcessor } from './ChatCommandProcessor';
import { isAbilityTrained } from '../../shared/progression/abilityCurve';
import { isPeacefulHotbarAbility } from '../../shared/logic/peacefulHotbar';
import { getAttackRange } from '../../shared/logic/AttackRange';
import { RaceGateStore } from '../systems/race/raceGateStore';
import { cloneActionBar } from '../systems/player/hotbarPress';
import { toggleRaceDebugOverlay, toggleRenderHitboxes, toggleRenderPowerupBoxes, toggleRenderCheckpoints } from '../systems/race/RaceDebugOverlayState';

export interface ActionDispatcherDeps {
  getPlayer: () => Helix2000Character | null;
  getGameWorld: () => GameScene | null;
  setPlayer: (updater: (prev: Helix2000Character | null) => Helix2000Character | null) => void;
  handleDevAction: (action: string, data?: any) => void;
  chatProcessor: ChatCommandProcessor;
}

export class ActionDispatcher {
  constructor(private deps: ActionDispatcherDeps) {}

  /** Update dependencies (called when React state changes) */
  updateDeps(deps: Partial<ActionDispatcherDeps>): void {
    Object.assign(this.deps, deps);
  }

  /** Dispatch an action by name */
  dispatch(action: string, data?: any): void {
    if (data !== undefined) {
      gameLogger.debug('stats', '🎮 [Game] handleAction called:', { action, data });
    } else {
      gameLogger.debug('stats', '🎮 [Game] handleAction called:', { action });
    }

    // Diagnostic-window toggles — driven straight through the windowBridge seam
    // (same path GameUIOverlay.handleActionWrapper, useGlobalInput and the
    // /tracker chat command use). Routed BEFORE the player/world guard below
    // because these windows don't need a spawned player/world (DevTools can be
    // open pre-spawn) — matching GameUIOverlay.handleActionWrapper's guard-free
    // behavior. Window ids verified against the WindowId union (types.ts).
    switch (action) {
    case 'toggle_ability_check': windowBridge.toggle('targetAbilityCheck'); return;
    case 'toggle_lifecycle_monitor': windowBridge.toggle('abilityLifecycle'); return;
    case 'toggle_visual_lifecycle': windowBridge.toggle('visualLifecycle'); return;
    case 'toggle_target_lifecycle': windowBridge.toggle('targetLifecycle'); return;
    case 'toggle_test_rig': windowBridge.toggle('abilityTestRig'); return;
    case 'toggle_netobs_packet_log': windowBridge.toggle('netobsPacketLog'); return;
    case 'toggle_netobs_latency_heatmap': windowBridge.toggle('netobsLatencyHeatmap'); return;

    // GM Race debug overlays. Module-singleton flips (RaceDebugOverlayState) —
    // handled inline like the window toggles above, and routed BEFORE the
    // player/world guard because none of them need a spawned player. They were
    // reachable ONLY from the React RenderToggles widget, so a headless VQA
    // driver could not turn them on at all; now they ride the same action
    // channel (and therefore the no-dead-devtools-action guard) as every other
    // debug toggle. The React buttons flip the same singletons, so the two
    // entry points cannot disagree.
    case 'toggle_race_debug': toggleRaceDebugOverlay(); return;
    case 'toggle_race_hitboxes': toggleRenderHitboxes(); return;
    case 'toggle_race_powerup_boxes': toggleRenderPowerupBoxes(); return;
    case 'toggle_race_checkpoints': toggleRenderCheckpoints(); return;
    }

    const player = this.deps.getPlayer();
    const gameWorld = this.deps.getGameWorld();
    if (!player || !gameWorld) return;

    switch (action) {
    case 'jump':
      gameWorld.handleJump();
      break;

    case 'attack_basic':
      this.handleClassAttack('basic');
      break;
    case 'attack_special':
      this.handleClassAttack('special');
      break;
    case 'change_map':
      this.deps.handleDevAction(action, data);
      break;
    case 'toggle_devtools':
      this.deps.handleDevAction('toggle_devtools', data);
      break;
    case 'toggle_logger':
      this.deps.handleDevAction('toggle_logger', data);
      break;
    case 'respawn':
      this.handleRespawn();
      break;
    case 'refresh_sprite':
      this.handleRefreshSprite();
      break;
    case 'save_player':
      this.handleSavePlayer(player, gameWorld);
      break;

    // Targeting actions
    case 'target_next':
      this.handleTargetingAction('next');
      break;
    case 'target_prev':
      this.handleTargetingAction('prev');
      break;
    case 'target_nearest':
      this.handleTargetingAction('nearest');
      break;
    case 'clear_target':
      this.handleTargetingAction('clear');
      break;
    case 'toggle_autoattack':
      this.handleTargetingAction('autoattack');
      break;

    case 'chat':
      this.handleChat(data, player);
      break;
    case 'clear_chat':
      chatHandler.clear();
      storage.remove('chat_history');
      gameLogger.info('chat', '🗑️ Chat history cleared');
      break;
    case 'useAbility':
      if (data && typeof data === 'string') {
        // Mirror the server's trained-list authority before local prediction.
        const progressionBypassed = player && (
          RaceGateStore.isExplicitlyAllowed(data)
          || isPeacefulHotbarAbility(
            (player as Helix2000Character & { peaceful?: boolean }).peaceful,
            data,
          )
        );
        if (player && !progressionBypassed && !isAbilityTrained(player.learnedAbilities, data)) {
          gameLogger.info('game', `⚠️ Ability ${data} is not trained`);
          return;
        }
        const registry = ServiceRegistry.getInstance();
        const entitySystem = registry.get('EntitySystem');
        entitySystem?.getPlayerManager()?.performAbility(data);
      }
      break;
    case 'updateActionBar':
    case 'moveActionBarSlot':
      this.handleUpdateActionBar(action, data);
      break;
    case 'editActionBarLayout':
      this.forwardServerAction('editActionBarLayout', data);
      break;
    case 'updateStats':
      this.handleUpdateStats(data);
      break;
    case 'useItem':
      // Audit D3 (2026-06-29) — client-side death check for immediate UX
      // feedback. The server (InventoryHandler.handleUseItem) also blocks
      // consumable use when dead; this prevents the request from being
      // sent at all so the player doesn't see a potion "used" then reverted.
      if (readPlayerVitals(player).health <= 0 || (player as any).isDead) {
        gameLogger.info('game', '⚠️ Cannot use item — player is dead');
        chatHandler.systemMessage('Cannot use items while dead. Wait until you respawn.');
        return;
      }
      this.handleUseItem(data);
      break;

    // NPC dialogue / quest / shop — forwarded to the matching server message
    // handlers (NPCInteractionHandler + QuestHandler) via the PLAYER_ACTION_INTENT
    // bridge in NetworkSyncSystem, which sends room.send(action, payload).
    case 'talkToNPC':
      this.forwardServerAction('talkToNPC', typeof data === 'string' ? { npcId: data } : data);
      break;
    case 'npcFerryTravel':
      this.forwardServerAction('npcFerryTravel', data);
      break;
    case 'acceptQuest':
      this.forwardServerAction('acceptQuest', typeof data === 'string' ? { questId: data } : data);
      break;
    case 'completeQuest':
      this.forwardServerAction('completeQuest', typeof data === 'string' ? { questId: data } : data);
      break;
    case 'buyItem':
      this.forwardServerAction('buyItem', data);
      break;
    case 'sellItem':
      this.forwardServerAction('sellItem', data);
      break;
    case 'dropItem': // plan 23 row 42: the server checks binding, bag and trade
      this.forwardServerAction('dropItem', data);
      break;
    case 'learnAbility':
      this.forwardServerAction('learnAbility', data);
      break;
    case 'spendTalent':
      this.forwardServerAction('spendTalent', data);
      break;
    case 'respecTalents':
      this.forwardServerAction('respecTalents', {});
      break;
    case 'respecStats':
      this.forwardServerAction('respecStats', {});
      break;

    // Dev actions pass-through
    case 'spawn':
    case 'spawn_mob':
    case 'spawn_test':
    case 'spawn_all_test':
    case 'teleport':
    case 'clear_mobs':
    case 'debug_state':
    case 'toggle_aggro_range':
    case 'connect_server':
    case 'disconnect':
    // Debug-overlay toggles — each has a working handler in useDevActions that
    // targets the DebugOverlaySystem (registry-resolved). They were dropping
    // through to `default` (Unknown HUD action) because they were never cased
    // here, so the DevTools "hitbox / velocity / physics-state / collision-flash"
    // buttons were silent no-ops. Routed like toggle_aggro_range.
    // toggle_melee_cone (the melee swing-cone hitbox) rides the same channel.
    case 'toggle_hitboxes':
    case 'toggle_velocity':
    case 'toggle_physics_state':
    case 'toggle_collision_flash':
    case 'toggle_melee_cone':
    // Player-state dev actions — handlers already live in useDevActions; the
    // matching DevTools buttons (Heal Full / Mana Full / Level Up / Add XP /
    // Force Sync Stats / Randomize Look / God Mode / Noclip / Combat Log) and
    // their helixDebugConsole twins routed here but had no case → dead.
    case 'heal':
    case 'mana':
    case 'levelup':
    case 'addxp':
    case 'force_sync_stats':
    case 'randomize_appearance':
    case 'toggle_god_mode':
    case 'toggle_noclip':
    case 'toggle_combat_log':
    case 'toggle_dev_action_logs':
    // Editor / class / cheat / test DevTools buttons whose useDevActions
    // handlers exist but were never routed here, so they hit `default`
    // (Unknown HUD action) — the Map platform tools, class switcher, editor
    // toggle, speed-multiplier dial and floating-text spawner were all dead
    // no-ops. Same revival as the 2026-06-18 overlay/player-action pass.
    case 'add_platform':
    case 'clear_platforms':
    case 'delete_last_platform':
    case 'set_class':
    case 'toggle_editor':
    case 'set_speed_mult':
    case 'show_floating_text':
      this.deps.handleDevAction(action, data);
      break;
    default:
      gameLogger.warn('game', `Unknown HUD action: ${action}`);
    }
  }

  // --- Private helpers ---

  /**
   * Forward an action straight to the server as a Colyseus message. The
   * NetworkSyncSystem PLAYER_ACTION_INTENT bridge does `room.send(action, data)`,
   * so the `action` here must match a server `onMessage(<action>)` handler.
   */
  private forwardServerAction(action: string, data?: unknown): void {
    EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, { action, data });
  }

  private handleRespawn(): void {
    const registry = ServiceRegistry.getInstance();
    const entitySystem = registry.get('EntitySystem');
    if (entitySystem) {
      gameLogger.info('game', '🔄 Triggering respawn via EntitySystem...');
      entitySystem.getPlayerManager()?.respawnPlayer();
    } else {
      gameLogger.warn('game', 'Cannot respawn - EntitySystem not available');
    }
  }

  private handleRefreshSprite(): void {
    gameLogger.info('game', '🔄 Refreshing sprite requested via UI');
    const registry = ServiceRegistry.getInstance();
    const entitySystem = registry.get('EntitySystem');
    entitySystem?.getPlayerManager()?.forceRefreshAppearance();
  }

  private handleSavePlayer(player: Helix2000Character, gameWorld: GameScene): void {
    gameLogger.info('game', '💾 Manual Save Requested');
    if (true) {
      EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, { action: 'save_player', data: {} });
      if (gameWorld && player) {
        gameWorld.showChatBubble(player.id, 'Saved!');
      }
    } else {
      gameLogger.warn('game', '⚠️ Cannot save: Not connected to server.');
      alert('Cannot save: Offline / No Server Connection');
    }
  }

  private handleTargetingAction(action: string): void {
    const gameWorld = this.deps.getGameWorld();
    if (!gameWorld) return;

    const registry = ServiceRegistry.getInstance();
    const targetingSystem = registry.get('TargetingSystem');

    if (!targetingSystem) {
      gameLogger.warn('game', '[Game] TargetingSystem not found');
      return;
    }

    switch (action) {
    case 'next':
      targetingSystem.cycleTarget('next');
      break;
    case 'prev':
      targetingSystem.cycleTarget('prev');
      break;
    case 'nearest':
      targetingSystem.targetNearest();
      break;
    case 'clear':
      targetingSystem.clearTarget();
      break;
    case 'autoattack':
      gameLogger.debug('game', '[Game] Auto-attack toggle requested');
      break;
    default:
      gameLogger.warn('game', `[Game] Unknown targeting action: ${action}`);
    }
  }

  private handleChat(data: any, player: Helix2000Character): void {
    if (data && typeof data === 'string') {
      // Client-side cleanup command
      if (data.trim() === '/clear') {
        chatHandler.clear();
        storage.remove('chat_history');
        return;
      }

      // Route slash commands to ChatCommandProcessor
      if (data.startsWith('/')) {
        this.deps.chatProcessor.process(data);
        return;
      }

      EventBus.getInstance().emit(EVENTS.PLAYER_CHAT_INTENT, { message: data });
      if (true) {
        // Local echo if not connected for debugging
        chatHandler.sendMessage(data, player.name, 'global');
      }
    }
  }

  /** A slot assignment, or a slot-to-slot move (one atomic swap on the server, D2). */
  private handleUpdateActionBar(action: string, data: any): void {
    if (data && typeof data === 'object') {
      const registry = ServiceRegistry.getInstance();
      const entitySystem = registry.get('EntitySystem');
      const pm = entitySystem?.getPlayerManager();

      if (action === 'moveActionBarSlot') {
        pm?.moveActionBarSlot(data.from, data.to);
      } else {
        const assignment = typeof data.abilityId === 'string' && data.abilityId
          ? { type: 'ability' as const, abilityId: data.abilityId }
          : typeof data.itemId === 'string' && data.itemId
            ? { type: 'item' as const, itemId: data.itemId }
            : null;
        const target = typeof data.bar === 'number' && typeof data.page === 'number'
          ? { bar: data.bar, page: data.page, slot: data.slotIndex }
          : data.slotIndex;
        pm?.updateActionBarSlot(target, assignment);
      }

      // Force React UI update by cloning the player
      const bar = pm?.getPlayer()?.actionBar;
      if (bar) {
        this.deps.setPlayer((prev) => (prev
          ? { ...prev, actionBar: cloneActionBar(bar) } as unknown as Helix2000Character
          : prev));
      }
    }
  }

  private handleUpdateStats(data: any): void {
    if (data && data.stat) {
      const amount = typeof data.amount === 'number' && data.amount > 0 ? data.amount : undefined;
      this.forwardServerAction('updateStats', { action: 'increase', stat: data.stat, ...(amount ? { amount } : {}) });
      // Do not optimistically mutate the React player mirror. The synced
      // Player schema is authoritative for both the point balance and the
      // recalculated primary/derived stats, including server rejection when
      // no points remain.
      gameLogger.info('stats', `📊 Sent authoritative stat update request: ${data.stat}${amount ? ` x${amount}` : ''}`);
    }
  }

  private handleUseItem(data: any): void {
    if (data && data.itemId) {
      EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, { action: 'useItem', data: { itemId: data.itemId } });
      gameLogger.info('game', `🧪 Sent useItem request: ${data.itemId}`);
    }
  }

  public getClassEmoji(characterClass: string): string {
    // Routed through the CLASS_ICONS SSOT — this switch was missing the
    // `rogue` (and `dev`) cases and fell through to '❓'; classIcon has them.
    return classIcon(characterClass);
  }

  public getClassAttackName(characterClass: string, attackType: 'basic' | 'special'): string {
    const attacks: Record<string, { basic: string, special: string }> = {
      warrior: { basic: 'Slash', special: 'Sword Throw' },
      mage: { basic: 'Arcane Bolt', special: 'Arcane Barrage' },
      hunter: { basic: 'Quick Shot', special: 'Spread Shot' },
      paladin: { basic: 'Holy Strike', special: 'Hammer Throw' }
    };
    const classAttacks = attacks[characterClass];
    if (classAttacks) {
      return attackType === 'basic' ? classAttacks.basic : classAttacks.special;
    }
    return attackType === 'basic' ? 'Attack' : 'Special';
  }

  public handleClassAttack(attackType: 'basic' | 'special'): void {
    const player = this.deps.getPlayer();
    const gameWorld = this.deps.getGameWorld();
    if (!player || !gameWorld) return;

    // Range from the TOML-derived SSOT, never a local class list.
    // This site used to read `player.class === 'hunter' || player.class ===
    // 'mage'` → 800 : 120 — the exact hardcode `AttackRange.ts` was built to
    // kill, and worse than the original because it named 2 of 9 classes:
    // warlock and druid have ranged basics and were acquiring targets in a
    // 120px window here. It is reachable only through the dev-action channel
    // (`__helixDebug.action('attack_basic')`), which is precisely the channel
    // agents measure combat from — so it was a false-bug generator aimed at
    // ourselves (D5, docs/audits/2026-07-26-debug-geometry-consolidation-research.md).
    const attackRange = getAttackRange(player.class);

    // Auto-target nearest enemy within range
    const targetInfo = gameWorld.getTarget(attackRange);
    const targetId = targetInfo ? targetInfo.id : null;

    // NOTE: a stale `CombatSystem.processAttack` call lived here — CombatSystem
    // never had that method (the real basic/special attack path is
    // PlayerManager → CombatManager.processAttack). It was a
    // silently-dead optional-chained no-op; the typed registry surfaced it.
    // Removed (behaviour-identical) rather than re-routed, since CombatManager
    // already fires from the live path and re-routing would double-attack.

    const canonicalAbilityIds = (player.abilities || [])
      .map((ability: any) => this.toCanonicalAbilityId(ability?.id))
      .filter((abilityId): abilityId is string => !!abilityId);
    const basicAbilityId = canonicalAbilityIds[0] || 'basic_attack';
    const specialAbilityId = canonicalAbilityIds[1] || canonicalAbilityIds[0] || 'special_attack';
    const abilityId = attackType === 'basic' ? basicAbilityId : specialAbilityId;
    const manaCost = attackType === 'basic' ? 0 : 10;

    EventBus.getInstance().emit(EVENTS.PLAYER_ACTION_INTENT, {
      action: 'castAbility',
      data: {
        ability_id: abilityId,
        client_cast_seq: nextClientCastSequence(),
        manaCost,
        attack: 10,
        target_id: targetId
      }
    });
  }

  private toCanonicalAbilityId(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const normalized = value.trim().toLowerCase();
    return /^[a-z][a-z0-9_]*$/.test(normalized) ? normalized : null;
  }

}
